import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SubscriptionsManager } from '../../../../shared/util/subscriptions-manager';
import { OauthClientDTO, OauthClientsService } from '../../../../shared/oauth-oidc-client/oauth-clients.service';
import { finalize } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OauthClientEditModalComponent } from './oauth-client-edit-modal.component';
import { id } from '@swimlane/ngx-charts';
import { AlertService } from '../../../../shared/alert/alert.service';
import { ModalConfirmComponent } from '../../../../shared/modal/modal-confirm.component';
import { AuthConfig, OauthConfigService } from '../../../../shared/oauth-oidc-client/oauth-config.service';
import { Pageable } from '../../../../shared/model/pageable';

@Component({
    selector: 'jhi-oauth-clients-pane',
    templateUrl: './oauth-clients-pane.component.html',
    styleUrl: './oauth-clients-pane.component.scss'
})
export class OauthClientsPaneComponent implements OnInit, OnDestroy {

    itemsPerPage: any = 20;
    page: number = 1;
    message: string;
    predicate: any = ColumnLabels.ID;
    previousPage: number;
    reverse: boolean = true;
    totalItems: number;

    oauthClients: OauthClientDTO[];
    isLoading: boolean;

    oauthConfig: AuthConfig;

    subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();

    constructor(private oauthClientsService: OauthClientsService,
                public translateService: TranslateService,
                private modalService: NgbModal,
                private alertService: AlertService,
                private oauthConfigService: OauthConfigService) {
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
    }

    ngOnInit(): void {
        // get all oauth clients (with pagination)
        this.loadAll();

        this.oauthConfigService.get().then((oauthConfig) => {
            this.oauthConfig = oauthConfig;
        });

    }

    addOAuthClient() {
        const modalRef = this.modalService.open(OauthClientEditModalComponent as Component, { modalDialogClass: 'modal-md', backdrop: 'static' });
        modalRef.componentInstance.oauthClient = {};
        modalRef.result.then((value) => {
            this.loadAll();
        });
    }

    editOAuthClient(oauthClient: OauthClientDTO) {

        const modalRef = this.modalService.open(OauthClientEditModalComponent as Component, { modalDialogClass: 'modal-md', backdrop: 'static' });
        modalRef.componentInstance.oauthClient = oauthClient;
        modalRef.result.then((value) => {
            this.loadAll();
        });
    }

    deleteOAuthClient(oauthClient: OauthClientDTO) {

        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message =  this.translateService.instant('site-admin.oauth-clients.delete.confirm', { param: oauthClient.clientId });
        confirmModalRef.componentInstance.title = this.translateService.instant('confirmation');

        confirmModalRef.result.then(() => {
            this.subscriptionsManager.add = this.oauthClientsService.delete(oauthClient.id).subscribe(() => {
                this.alertService.success('site-admin.oauth-clients.delete.success', { param: oauthClient.clientId });
                this.loadAll();
            });
        });

    }

    loadPage(page: number) {
        this.page = page;
        this.loadAll();
    }

    loadAll() {
        this.isLoading = true;
        this.subscriptionsManager.add = this.oauthClientsService.getAll(
            <Pageable>({
                page: this.page - 1,
                size: this.itemsPerPage,
                sort: this.getSort()
            })).pipe(finalize(() => {
            this.isLoading = false;
        })).subscribe(
            (res: HttpResponse<OauthClientDTO[]>) => this.onSuccess(res.body, res.headers)
        );
    }

    transition() {
        this.loadAll();
    }

    private onSuccess(data: any[], headers) {
        this.totalItems = headers.get('X-Total-Count');
        this.oauthClients = data;
    }

    private getSort() {
        if (!this.predicate) {
            return '';
        }
        return [this.predicate + ',' + (this.reverse ? 'asc' : 'desc')];
    }

    protected readonly id = id;
    protected readonly ColumnLabels = ColumnLabels;
}

export enum ColumnLabels {
    'ID' = 'id',
    'CLIENT_ID' = 'clientId',
    'REDIRECT_URI' = 'redirectUri',
    'DESCRIPTION' = 'description',
}
